import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  addToast,
  Button,
  Checkbox,
  Divider,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from '@heroui/react';
import { Search } from 'lucide-react';

import {
  useAddProductRepository,
  useProducts,
  useRemoveProductRepository,
} from '~/hooks/useProducts';
import { useRepositories } from '~/hooks/useRepositories';

import { Input } from '~/components/Field';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { Modal } from '~/components/Modal';

import type { Repository } from '~/types';

interface RepoRowProps {
  isDisabled: boolean;
  isLinked: boolean;
  onToggle: (repository: Repository, shouldLink: boolean) => void;
  otherProductNames: string[];
  repository: Repository;
}

export interface LinkReposModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
}

/** Sentinel key for the "All owners" filter option — HeroUI Select keys can't be empty strings. */
const ALL_OWNERS = '__all__';

/** Case-insensitive match against fullName, name, and description — everything a
 * user would recognize a repo by. */
function matchesSearch(repository: Repository, query: string): boolean {
  if (!query.trim()) return true;
  const haystack =
    `${repository.fullName} ${repository.name} ${repository.description ?? ''}`.toLowerCase();
  return haystack.includes(query.trim().toLowerCase());
}

/**
 * Column headers for the repo list — mirrors RepositoriesModal's table
 * header idiom. The leading `w-8` column holds a "select all" checkbox
 * (indeterminate when only some of the currently filtered repos are linked)
 * instead of a bare spacer, so it exactly matches RepoRow's checkbox column
 * and "Repository" lines up with the names below it.
 */
function RepoListHeader({
  allSelected,
  isDisabled,
  onSelectAllChange,
  someSelected,
}: {
  allSelected: boolean;
  isDisabled: boolean;
  onSelectAllChange: (shouldLinkAll: boolean) => void;
  someSelected: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center gap-3 border-b border-default px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-muted">
      <div className="flex w-8 shrink-0 items-center justify-center">
        <Checkbox
          aria-label="Select all repositories"
          isDisabled={isDisabled}
          isIndeterminate={someSelected && !allSelected}
          isSelected={allSelected}
          onValueChange={onSelectAllChange}
        />
      </div>
      <div className="w-64 shrink-0">Repository</div>
      <div className="min-w-0 flex-1">Description</div>
    </div>
  );
}

function RepoRow({
  isDisabled,
  isLinked,
  onToggle,
  otherProductNames,
  repository,
}: RepoRowProps): JSX.Element {
  const handleRowClick = (): void => {
    if (isDisabled) return;
    onToggle(repository, !isLinked);
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- mouse convenience only; the nested Checkbox below is the real, fully keyboard-accessible control
    <div
      className="group/repo-row flex cursor-pointer items-start gap-3 rounded-md px-2 py-2 hover:bg-surface-elevated"
      data-testid={`repo-row-${repository.id}`}
      onClick={handleRowClick}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- stops the row's click-to-toggle above from double-firing when the checkbox itself is clicked */}
      <div
        className="flex w-8 shrink-0 items-center justify-center"
        onClick={e => e.stopPropagation()}
      >
        <Checkbox
          aria-label={`${isLinked ? 'Unlink' : 'Link'} ${repository.fullName}`}
          classNames={{
            // The row's own hover background makes the checkbox's default hover
            // fill blend in and vanish. Force a visible primary-colored border
            // whenever the row (not just the checkbox itself) is hovered, via a
            // named group so it doesn't collide with HeroUI's own internal
            // `group` class on the checkbox's base element.
            wrapper:
              'group-data-[hover=true]:before:bg-transparent group-hover/repo-row:before:border-primary',
          }}
          isDisabled={isDisabled}
          isSelected={isLinked}
          onValueChange={shouldLink => onToggle(repository, shouldLink)}
        />
      </div>
      <div className="w-64 shrink-0">
        <p className="text-xs text-muted">{repository.owner}</p>
        <p className="text-sm text-foreground">{repository.name}</p>
        {otherProductNames.length > 0 && (
          <p className="text-xs text-muted">also in {otherProductNames.join(', ')}</p>
        )}
      </div>
      <div className="min-w-0 flex-1">
        {repository.description && (
          <p className="break-words text-xs text-muted">{repository.description}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Searchable per-product repository link/unlink manager.
 *
 * Edits are staged locally (checking/unchecking never hits the network) and
 * only committed on "Save", which diffs the staged selection against the
 * snapshot taken when the modal opened and applies just the additions/
 * removals. "Reset" reverts the staged selection back to that snapshot, and
 * "Cancel"/closing the modal discards it — nothing is written until Save.
 *
 * Deliberately has no "Connect New Repository" or manual-entry affordance —
 * repository registration lives elsewhere; this modal only manages the
 * product<->repository association for repos that already exist.
 *
 * Pattern: docs/design-patterns/frontend/anti-patterns/request-fan-out.md —
 * "also in" and linked/available counts are derived from the single
 * useRepositories() list, never fetched per-row.
 */
export function LinkReposModal({ isOpen, onClose, productId }: LinkReposModalProps): JSX.Element {
  const { data: repositories, isError, isLoading } = useRepositories();
  const { data: products } = useProducts();
  const addProductRepository = useAddProductRepository();
  const removeProductRepository = useRemoveProductRepository();

  const [searchQuery, setSearchQuery] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Snapshot of which repos were linked when this session opened (the Save
  // baseline) and the staged selection the user is editing. Both are cleared
  // on close and recaptured fresh next time the modal opens.
  const [originalRepoIds, setOriginalRepoIds] = useState<string[] | null>(null);
  const [selectedRepoIds, setSelectedRepoIds] = useState<string[] | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setOriginalRepoIds(null);
      setSelectedRepoIds(null);
      return;
    }
    if (repositories) {
      const linkedIds = repositories
        .filter(repository => repository.productIds?.includes(productId))
        .map(repository => repository.id);
      setOriginalRepoIds(prev => prev ?? linkedIds);
      setSelectedRepoIds(prev => prev ?? linkedIds);
    }
  }, [isOpen, productId, repositories]);

  const isReady = !isLoading && !isError && selectedRepoIds !== null;

  const product = products?.find(p => p.id === productId);

  const productNameById = useMemo(() => {
    const map = new Map<string, string>();
    products?.forEach(p => map.set(p.id, p.name));
    return map;
  }, [products]);

  const owners = useMemo(
    () =>
      [...new Set((repositories ?? []).map(repository => repository.owner))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [repositories],
  );

  const filteredRepos = useMemo(
    () =>
      (repositories ?? []).filter(
        repository =>
          (!ownerFilter || repository.owner === ownerFilter) &&
          matchesSearch(repository, searchQuery),
      ),
    [repositories, searchQuery, ownerFilter],
  );

  const totalCount = repositories?.length ?? 0;
  const selectedCount = selectedRepoIds?.length ?? 0;
  const availableCount = totalCount - selectedCount;

  const hasChanges = useMemo(() => {
    if (!originalRepoIds || !selectedRepoIds) return false;
    if (originalRepoIds.length !== selectedRepoIds.length) return true;
    const originalSet = new Set(originalRepoIds);
    return selectedRepoIds.some(id => !originalSet.has(id));
  }, [originalRepoIds, selectedRepoIds]);

  const handleToggle = (repository: Repository, shouldLink: boolean): void => {
    setSelectedRepoIds(prev => {
      if (!prev) return prev;
      return shouldLink ? [...prev, repository.id] : prev.filter(id => id !== repository.id);
    });
  };

  // "Select all" always targets the currently filtered/searched set, not the
  // full repo list — narrowing the list and selecting all shouldn't reach
  // into repos the search/owner filter is hiding.
  const allFilteredSelected =
    filteredRepos.length > 0 &&
    filteredRepos.every(repository => selectedRepoIds?.includes(repository.id));
  const someFilteredSelected = filteredRepos.some(repository =>
    selectedRepoIds?.includes(repository.id),
  );

  const handleSelectAllChange = (shouldSelectAll: boolean): void => {
    const filteredIds = new Set(filteredRepos.map(repository => repository.id));
    setSelectedRepoIds(prev => {
      if (!prev) return prev;
      return shouldSelectAll
        ? [...new Set([...filteredIds, ...prev])]
        : prev.filter(id => !filteredIds.has(id));
    });
  };

  const handleReset = (): void => {
    setSelectedRepoIds(originalRepoIds);
  };

  const handleClose = (): void => {
    if (isSaving) return;
    onClose();
  };

  const handleSave = async (): Promise<void> => {
    if (!originalRepoIds || !selectedRepoIds) return;
    const originalSet = new Set(originalRepoIds);
    const selectedSet = new Set(selectedRepoIds);
    const added = selectedRepoIds.filter(id => !originalSet.has(id));
    const removed = originalRepoIds.filter(id => !selectedSet.has(id));
    if (added.length === 0 && removed.length === 0) {
      onClose();
      return;
    }

    setIsSaving(true);
    const repoById = new Map((repositories ?? []).map(repository => [repository.id, repository]));
    const succeededIds = new Set<string>();

    await Promise.all(
      [
        ...added.map(id => ({ id, shouldLink: true })),
        ...removed.map(id => ({ id, shouldLink: false })),
      ].map(async ({ id, shouldLink }) => {
        const mutation = shouldLink ? addProductRepository : removeProductRepository;
        try {
          await mutation.mutateAsync({ productId, repositoryId: id });
          succeededIds.add(id);
        } catch {
          addToast({
            color: 'danger',
            description: `Failed to ${shouldLink ? 'link' : 'unlink'} ${repoById.get(id)?.fullName ?? 'repository'}. Please try again.`,
          });
        }
      }),
    );

    setIsSaving(false);

    if (succeededIds.size === added.length + removed.length) {
      onClose();
      return;
    }

    // Only fold the repos that actually changed into the new baseline, so a
    // retry only re-attempts whatever failed instead of redoing everything.
    setOriginalRepoIds(prev => {
      if (!prev) return prev;
      const next = new Set(prev);
      added.forEach(id => succeededIds.has(id) && next.add(id));
      removed.forEach(id => succeededIds.has(id) && next.delete(id));
      return [...next];
    });
  };

  return (
    <Modal
      hideCloseButton
      isOpen={isOpen}
      maxWidth="3xl"
      placement="top"
      preventClosing={isSaving}
      onOpenChange={handleClose}
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-0.5">
          <span>Link Repositories</span>
          <span className="text-sm font-normal text-muted">
            Select the repositories that belong to {product?.name ?? 'this product'}.
          </span>
        </ModalHeader>
        <Divider />
        <ModalBody className="flex max-h-[60vh] flex-col gap-3 overflow-hidden">
          <div className="flex items-center gap-2">
            <Input
              aria-label="Search repositories"
              className="flex-1"
              id="link-repos-search"
              placeholder="Search repositories..."
              startContent={<Search aria-hidden="true" className="h-4 w-4 text-muted" />}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <Select
              aria-label="Filter by owner"
              className="w-40 shrink-0"
              disallowEmptySelection
              selectedKeys={[ownerFilter || ALL_OWNERS]}
              size="sm"
              onSelectionChange={keys =>
                setOwnerFilter(
                  ([...keys][0] as string) === ALL_OWNERS ? '' : ([...keys][0] as string),
                )
              }
            >
              {[
                <SelectItem key={ALL_OWNERS}>All owners</SelectItem>,
                ...owners.map(owner => <SelectItem key={owner}>{owner}</SelectItem>),
              ]}
            </Select>
          </div>

          {(isLoading || (!isError && !isReady)) && (
            <p className="text-sm text-muted">Loading repositories...</p>
          )}
          {isError && (
            <p className="text-sm text-error">
              Failed to load repositories. Please try again later.
            </p>
          )}
          {isReady && filteredRepos.length === 0 && (
            <p className="text-sm text-muted">
              {searchQuery.trim()
                ? 'No repositories match your search.'
                : 'No repositories found in this organization.'}
            </p>
          )}
          {isReady && filteredRepos.length > 0 && (
            <div className="min-h-0 flex-1 overflow-y-auto">
              <RepoListHeader
                allSelected={allFilteredSelected}
                isDisabled={isSaving}
                someSelected={someFilteredSelected}
                onSelectAllChange={handleSelectAllChange}
              />
              <div className="space-y-1 pt-1">
                {filteredRepos.map(repository => {
                  const isLinked = selectedRepoIds?.includes(repository.id) ?? false;
                  const otherProductNames = (repository.productIds ?? [])
                    .filter(id => id !== productId)
                    .map(id => productNameById.get(id))
                    .filter((name): name is string => !!name);

                  return (
                    <RepoRow
                      key={repository.id}
                      isDisabled={isSaving}
                      isLinked={isLinked}
                      otherProductNames={otherProductNames}
                      repository={repository}
                      onToggle={handleToggle}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </ModalBody>
        <Divider />
        <ModalFooter className="justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted">
              {selectedCount} linked &middot; {availableCount} available
            </span>
            <Button
              isDisabled={isSaving || !hasChanges}
              size="sm"
              variant="light"
              onPress={handleReset}
            >
              Reset
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button isDisabled={isSaving} variant="light" onPress={handleClose}>
              Cancel
            </Button>
            <Button
              aria-busy={isSaving}
              color="primary"
              isDisabled={isSaving || !hasChanges}
              onPress={() => void handleSave()}
            >
              {isSaving ? <LoadingSpinner /> : 'Save'}
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
